/**
 * Catálogo do domínio `biblioteca` (T53, CAT-02).
 *
 * Origem: `src/shared/autorizacao/Diretorio.ts`.
 *
 * Este é o domínio que **não cabe inteiro no schema de `Concessao`**, e é a razão
 * de `concessoesCustomizadas` existir no plano (T58). O motivo é concreto: as
 * cinco regras de ACL de `Diretorio` comparam um campo do REGISTRO contra uma
 * lista derivada do USUÁRIO (`user.todasPermissoes` — papéis, e-mail e empresas).
 * `Concessao.condicao` é um objeto fixo por papel; não há como fixar nele um
 * valor que muda a cada usuário.
 *
 * Não é ABAC por relação (esse é o `RELACOES` da T51, onde o campo do registro é
 * comparado ao e-mail): aqui o registro guarda uma **lista de quem tem cada
 * nível de acesso àquela pasta**, e a pergunta é se alguma identidade do usuário
 * está nela. É lista de controle de acesso por registro, e é declaradamente a
 * exceção do catálogo (`ADR-0003`, "diretorio.ts — exceção declarada").
 */
import type { Concessao, NomePapel, Papel } from "../catalogo/vocabulario";
import type { RegraCompilada } from "../compilador";

/**
 * O que qualquer autenticado recebe (`Diretorio.ts:8-9,20`).
 *
 * A terceira entrada é a única regra de `Diretorio` que **não** depende do
 * usuário: pasta sem lista `acesso.full` é pasta aberta, e `condicao` dá conta
 * dela sem escape hatch.
 */
export const CONCESSOES_BASE_BIBLIOTECA: readonly Concessao[] = [
  { acoes: ["read"], recurso: "BIBLIOTECA" },
  { acoes: ["read"], recurso: "PERFIL" },
  {
    acoes: ["read", "upload", "novaPasta"],
    recurso: "Diretorio",
    condicao: { "acesso.full": { $exists: false } },
  },
];

export const PAPEIS_BIBLIOTECA = {
  "biblioteca.admin": {
    dominio: "biblioteca",
    nivel: "admin",
    concessoes: [{ acoes: ["manage"], recurso: "Diretorio" }],
  },
  /** Sem chave em `LEGADO`: a leitura da biblioteca é da base. Existe porque `GRUPOS.MKT` o cita. */
  "biblioteca.leitura": {
    dominio: "biblioteca",
    nivel: "leitura",
    concessoes: [{ acoes: ["read"], recurso: "BIBLIOTECA" }],
  },
} as const satisfies Partial<Record<NomePapel, Papel>>;

/** Os cinco níveis de acesso que uma pasta declara, e o que cada um libera. */
const NIVEIS_DE_PASTA = [
  { campo: "acesso.full", acoes: ["manage"] },
  { campo: "acesso.rOnly", acoes: ["read"] },
  { campo: "acesso.uploadOnly", acoes: ["read", "upload"] },
  { campo: "acesso.rW", acoes: ["read", "editar", "upload", "novaPasta", "delete"] },
  {
    campo: "acesso.rWEdit",
    acoes: ["read", "editar", "gerenciar", "upload", "novaPasta", "delete"],
  },
] as const;

/**
 * Escape hatch declarado: as regras de ACL de `Diretorio`, que dependem do
 * usuário e não cabem em `Concessao`.
 *
 * Recebe as identidades do usuário (`user.todasPermissoes`) e emite as cinco
 * regras na mesma ordem e com as mesmas ações de `Diretorio.ts:11-31`. A T58
 * conta quantos papéis usam o hatch e reprova quando o número cresce: cada uso
 * novo é sinal de schema faltando, não de exceção normal.
 */
export function concessoesCustomizadasDeBiblioteca(
  identidades: readonly string[],
): RegraCompilada[] {
  return NIVEIS_DE_PASTA.map((nivel) => ({
    action: [...nivel.acoes],
    subject: "Diretorio" as const,
    conditions: { [nivel.campo]: { $in: identidades } },
  }));
}
